import React, { useState, useEffect } from "react";
import { 
    Card, Row, Col, Button, Spinner, Alert, Table,
    Form, Badge, Pagination, OverlayTrigger, Tooltip
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { formatCurrency, formatNumber } from "../utils/formatters";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";
import ChartCard from "./ChartCard";

export default function EvolutionMensuelle() {
    const navigate = useNavigate();
    const [data, setData] = useState([]);
    const [annees, setAnnees] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [annee, setAnnee] = useState(new Date().getFullYear());
    const [viewMode, setViewMode] = useState('table');
    const [itemsPerPage, setItemsPerPage] = useState(12);
    const [currentPage, setCurrentPage] = useState(1);
    const [sortConfig, setSortConfig] = useState({ key: 'mois', direction: 'asc' });

    useEffect(() => {
        fetchAnnees();
        fetchData();
    }, [annee]);

    const fetchAnnees = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                'https://sawd.julesferry-ga.com/api/stats.php?action=annees',
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            
            if (response.ok) {
                const result = await response.json();
                setAnnees(result.data || []);
            }
        } catch (err) {
            console.error("Erreur chargement années", err);
        }
    };

    const fetchData = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("token");
            const response = await fetch(
                `https://sawd.julesferry-ga.com/api/stats.php?action=evolution_mensuelle&annee=${annee}`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            
            if (!response.ok) throw new Error("Erreur chargement");
            
            const result = await response.json();
            setData(result.data || []);
            setCurrentPage(1);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const mois = [
        'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
        'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
    ];

    // Tri des données
    const handleSort = (key) => {
        setSortConfig(prev => ({
            key,
            direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
        }));
    };

    const getMoisDonnees = () => {
        return data.map((item, index) => ({
            ...item,
            moisLabel: mois[item.mois - 1] || `Mois ${item.mois}`,
            moisNum: item.mois,
            ca: parseFloat(item.ca) || 0,
            nb_factures: parseInt(item.nb_factures) || 0,
            nb_clients: parseInt(item.nb_clients) || 0,
            panier_moyen: parseFloat(item.panier_moyen) || 0
        }));
    };

    const donneesAvecMois = getMoisDonnees();

    // Tri et pagination
    const sortedData = React.useMemo(() => {
        const sortableData = [...donneesAvecMois];
        if (sortConfig.key) {
            sortableData.sort((a, b) => {
                let aVal = a[sortConfig.key];
                let bVal = b[sortConfig.key];
                
                if (sortConfig.key === 'moisLabel') {
                    aVal = a.moisNum;
                    bVal = b.moisNum;
                }
                
                if (aVal < bVal) {
                    return sortConfig.direction === 'asc' ? -1 : 1;
                }
                if (aVal > bVal) {
                    return sortConfig.direction === 'asc' ? 1 : -1;
                }
                return 0;
            });
        }
        return sortableData;
    }, [donneesAvecMois, sortConfig]);

    // Pagination
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = sortedData.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(sortedData.length / itemsPerPage);

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
        document.querySelector('.table-card')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    const totalCA = sortedData.reduce((sum, item) => sum + (item.ca || 0), 0);
    const totalFactures = sortedData.reduce((sum, item) => sum + (item.nb_factures || 0), 0);
    const totalClients = sortedData.reduce((sum, item) => sum + (item.nb_clients || 0), 0);
    const caMoyen = totalFactures > 0 ? totalCA / totalFactures : 0;
    const maxCA = Math.max(...sortedData.map(d => d.ca), 1);
    const moisAvecCA = sortedData.filter(d => d.ca > 0).length;

    const moisAbrege = (moisComplet) => {
        return moisComplet.substring(0, 3);
    };

    if (loading) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4">
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
                        <div className="text-center">
                            <Spinner animation="border" variant="primary" className="mb-3" style={{ width: '3rem', height: '3rem' }} />
                            <p className="text-muted">Chargement de l'évolution mensuelle...</p>
                        </div>
                    </div>
                </div>
            </>
        );
    }

    if (error) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4">
                    <Alert variant="danger" className="text-center py-5">
                        <i className="ri-error-warning-line fs-48 mb-3 d-block"></i>
                        <h4>{error}</h4>
                        <Button variant="outline-danger" className="mt-3" onClick={fetchData}>
                            <i className="ri-refresh-line me-2"></i>Réessayer
                        </Button>
                    </Alert>
                </div>
            </>
        );
    }

    return (
        <>
            <Header />
            <div className="main main-app p-3 p-lg-4">
                <div className="container-fluid">
                    {/* En-tête moderne */}
                    <Row className="mb-4 fade-in">
                        <Col md={12}>
                            <div className="d-flex justify-content-between align-items-center header-section">
                                <div className="d-flex align-items-center gap-3">
                                    <div 
                                        className="page-icon-wrapper d-flex align-items-center justify-content-center" 
                                        style={{ 
                                            width: '70px', 
                                            height: '70px', 
                                            borderRadius: '20px',
                                            background: 'linear-gradient(135deg, rgba(67, 97, 238, 0.15), rgba(58, 86, 212, 0.15))',
                                            boxShadow: '0 10px 20px rgba(67, 97, 238, 0.15)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            flexShrink: 0
                                        }}
                                    >
                                        <i className="ri-bar-chart-2-line text-primary" style={{ fontSize: '36px', lineHeight: 1 }}></i>
                                    </div>
                                    <div>
                                        <h1 className="page-title mb-1" style={{ fontSize: '2rem', fontWeight: 700, color: '#1e293b' }}>
                                            Évolution mensuelle
                                        </h1>
                                        <p className="page-subtitle mb-0" style={{ fontSize: '1rem', color: '#64748b' }}>
                                            Analyse du chiffre d'affaires • Année {annee}
                                        </p>
                                    </div>
                                </div>
                                <div className="d-flex gap-2">
                                    <Button 
                                        variant={viewMode === 'table' ? 'primary' : 'outline-primary'}
                                        size="sm"
                                        onClick={() => setViewMode('table')}
                                        className="rounded-pill px-3"
                                    >
                                        <i className="ri-table-line me-1"></i>Tableau
                                    </Button>
                                    <Button 
                                        variant={viewMode === 'graph' ? 'primary' : 'outline-primary'}
                                        size="sm"
                                        onClick={() => setViewMode('graph')}
                                        className="rounded-pill px-3 me-2"
                                    >
                                        <i className="ri-bar-chart-2-line me-1"></i>Graphique
                                    </Button>
                                    <Button variant="outline-secondary" onClick={() => navigate('/stats/dashboard')} className="rounded-pill px-4">
                                        <i className="ri-arrow-left-line me-2"></i>Retour
                                    </Button>
                                </div>
                            </div>
                        </Col>
                    </Row>

                    {/* Cartes de statistiques */}
                    <Row className="g-3 mb-4 stats-row">
                        <Col md={3}>
                            <Card className="border-0 shadow-sm h-100 stat-card">
                                <Card.Body className="p-3 d-flex align-items-center">
                                    <div className="stat-icon-wrapper bg-primary-soft me-3">
                                        <i className="ri-money-euro-circle-line text-primary fs-4"></i>
                                    </div>
                                    <div>
                                        <small className="text-muted d-block text-uppercase fw-semibold tracking-wide">
                                            CA Annuel
                                        </small>
                                        <h5 className="mb-0 fw-bold text-primary">{formatCurrency(totalCA)}</h5>
                                        <small className="text-muted">total {annee}</small>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={3}>
                            <Card className="border-0 shadow-sm h-100 stat-card">
                                <Card.Body className="p-3 d-flex align-items-center">
                                    <div className="stat-icon-wrapper bg-success-soft me-3">
                                        <i className="ri-file-list-line text-success fs-4"></i>
                                    </div>
                                    <div>
                                        <small className="text-muted d-block text-uppercase fw-semibold tracking-wide">
                                            Factures
                                        </small>
                                        <h5 className="mb-0 fw-bold text-success">{formatNumber(totalFactures)}</h5>
                                        <small className="text-muted">documents</small>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={3}>
                            <Card className="border-0 shadow-sm h-100 stat-card">
                                <Card.Body className="p-3 d-flex align-items-center">
                                    <div className="stat-icon-wrapper bg-info-soft me-3">
                                        <i className="ri-group-line text-info fs-4"></i>
                                    </div>
                                    <div>
                                        <small className="text-muted d-block text-uppercase fw-semibold tracking-wide">
                                            Clients
                                        </small>
                                        <h5 className="mb-0 fw-bold text-info">{formatNumber(totalClients)}</h5>
                                        <small className="text-muted">acheteurs</small>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={3}>
                            <Card className="border-0 shadow-sm h-100 stat-card">
                                <Card.Body className="p-3 d-flex align-items-center">
                                    <div className="stat-icon-wrapper bg-warning-soft me-3">
                                        <i className="ri-shopping-basket-line text-warning fs-4"></i>
                                    </div>
                                    <div>
                                        <small className="text-muted d-block text-uppercase fw-semibold tracking-wide">
                                            Panier moyen
                                        </small>
                                        <h5 className="mb-0 fw-bold text-warning">{formatCurrency(caMoyen)}</h5>
                                        <small className="text-muted">par facture</small>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>

                    {/* Filtres modernes */}
                    <Card className="filter-card border-0 shadow-sm mb-4">
                        <Card.Body>
                            <Row className="g-3 align-items-end">
                                <Col md={3}>
                                    <Form.Group>
                                        <Form.Label className="fw-semibold text-muted small text-uppercase">
                                            <i className="ri-calendar-2-line me-2"></i>
                                            Année
                                        </Form.Label>
                                        <Form.Select 
                                            value={annee} 
                                            onChange={(e) => setAnnee(e.target.value)}
                                            className="filter-select py-2"
                                        >
                                            {annees.map(a => (
                                                <option key={a} value={a}>{a}</option>
                                            ))}
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group>
                                        <Form.Label className="fw-semibold text-muted small text-uppercase">
                                            <i className="ri-list-settings-line me-2"></i>
                                            Affichage
                                        </Form.Label>
                                        <Form.Select 
                                            value={itemsPerPage} 
                                            onChange={(e) => {
                                                setItemsPerPage(parseInt(e.target.value));
                                                setCurrentPage(1);
                                            }}
                                            className="filter-select py-2"
                                        >
                                            <option value="6">6 par page</option>
                                            <option value="12">12 par page</option>
                                            <option value="24">24 par page</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group>
                                        <Form.Label className="fw-semibold text-muted small text-uppercase">
                                            <i className="ri-sort-asc line me-2"></i>
                                            Trier par
                                        </Form.Label>
                                        <Form.Select 
                                            value={sortConfig.key}
                                            onChange={(e) => setSortConfig({ key: e.target.value, direction: 'asc' })}
                                            className="filter-select py-2"
                                        >
                                            <option value="moisLabel">Mois</option>
                                            <option value="ca">Chiffre d'affaires</option>
                                            <option value="nb_factures">Factures</option>
                                            <option value="panier_moyen">Panier moyen</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <div className="d-flex gap-2">
                                        <Button 
                                            variant="primary" 
                                            onClick={fetchData}
                                            className="flex-grow-1 d-flex align-items-center justify-content-center gap-2 py-2"
                                            style={{
                                                background: 'linear-gradient(135deg, #4361ee, #3a56d4)',
                                                border: 'none',
                                                borderRadius: '10px'
                                            }}
                                        >
                                            <i className="ri-refresh-line"></i>
                                            <span>Actualiser</span>
                                        </Button>
                                    </div>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    {/* Vue Tableau */}
                    {viewMode === 'table' && (
                        <Card className="border-0 shadow-sm table-card">
                            <Card.Header className="bg-white py-3 d-flex justify-content-between align-items-center">
                                <h5 className="mb-0">
                                    <i className="ri-calendar-line me-2 text-primary"></i>
                                    Détail mensuel {annee}
                                </h5>
                                <Badge bg="primary" className="px-3 py-2">
                                    {moisAvecCA} mois actifs sur 12
                                </Badge>
                            </Card.Header>
                            <Card.Body className="p-0">
                                <div className="table-responsive">
                                    <Table hover className="mb-0 align-middle custom-table">
                                        <thead className="bg-light">
                                            <tr>
                                                <th style={{ cursor: 'pointer' }} onClick={() => handleSort('moisLabel')}>
                                                    Mois <i className={`ri-arrow-${sortConfig.key === 'moisLabel' && sortConfig.direction === 'asc' ? 'up' : 'down'}-s-line ms-1`}></i>
                                                </th>
                                                <th className="text-end" style={{ cursor: 'pointer' }} onClick={() => handleSort('ca')}>
                                                    Chiffre d'affaires <i className={`ri-arrow-${sortConfig.key === 'ca' && sortConfig.direction === 'asc' ? 'up' : 'down'}-s-line ms-1`}></i>
                                                </th>
                                                <th className="text-end" style={{ cursor: 'pointer' }} onClick={() => handleSort('nb_factures')}>
                                                    Factures <i className={`ri-arrow-${sortConfig.key === 'nb_factures' && sortConfig.direction === 'asc' ? 'up' : 'down'}-s-line ms-1`}></i>
                                                </th>
                                                <th className="text-end">Clients</th>
                                                <th className="text-end" style={{ cursor: 'pointer' }} onClick={() => handleSort('panier_moyen')}>
                                                    Panier moyen <i className={`ri-arrow-${sortConfig.key === 'panier_moyen' && sortConfig.direction === 'asc' ? 'up' : 'down'}-s-line ms-1`}></i>
                                                </th>
                                                <th className="text-center">Performance</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {currentItems.map((item, index) => {
                                                const moisPrecedent = index > 0 ? sortedData[indexOfFirstItem + index - 1]?.ca : item.ca;
                                                const evolution = moisPrecedent > 0 
                                                    ? ((item.ca - moisPrecedent) / moisPrecedent * 100).toFixed(1) 
                                                    : 0;
                                                
                                                return (
                                                    <tr key={item.mois} style={{
                                                        animation: `fadeIn 0.3s ease ${index * 0.05}s both`
                                                    }}>
                                                        <td className="fw-semibold">
                                                            <span className="text-capitalize">{item.moisLabel}</span>
                                                        </td>
                                                        <td className="text-end fw-bold text-primary">
                                                            {formatCurrency(item.ca)}
                                                        </td>
                                                        <td className="text-end">
                                                            <Badge bg="info" className="rounded-pill px-3 py-2">
                                                                {item.nb_factures}
                                                            </Badge>
                                                        </td>
                                                        <td className="text-end">{item.nb_clients || '-'}</td>
                                                        <td className="text-end text-success fw-semibold">
                                                            {formatCurrency(item.panier_moyen)}
                                                        </td>
                                                        <td className="text-center">
                                                            {index > 0 && item.ca > 0 && (
                                                                <Badge 
                                                                    bg={evolution >= 0 ? 'success' : 'danger'}
                                                                    className="rounded-pill px-3 py-2"
                                                                >
                                                                    <i className={`ri-arrow-${evolution >= 0 ? 'up' : 'down'}-line me-1`}></i>
                                                                    {Math.abs(evolution)}%
                                                                </Badge>
                                                            )}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </Table>
                                </div>
                            </Card.Body>
                            
                            {/* Pagination */}
                            {totalPages > 1 && (
                                <Card.Footer className="bg-white py-3">
                                    <div className="d-flex justify-content-between align-items-center">
                                        <div className="text-muted small">
                                            Affichage de {indexOfFirstItem + 1} à {Math.min(indexOfLastItem, sortedData.length)} sur {sortedData.length} mois
                                        </div>
                                        
                                        <Pagination className="mb-0">
                                            <Pagination.First 
                                                onClick={() => handlePageChange(1)}
                                                disabled={currentPage === 1}
                                            />
                                            <Pagination.Prev 
                                                onClick={() => handlePageChange(currentPage - 1)}
                                                disabled={currentPage === 1}
                                            />
                                            
                                            {[...Array(Math.min(5, totalPages))].map((_, i) => {
                                                let pageNum;
                                                if (totalPages <= 5) {
                                                    pageNum = i + 1;
                                                } else if (currentPage <= 3) {
                                                    pageNum = i + 1;
                                                } else if (currentPage >= totalPages - 2) {
                                                    pageNum = totalPages - 4 + i;
                                                } else {
                                                    pageNum = currentPage - 2 + i;
                                                }
                                                
                                                return (
                                                    <Pagination.Item
                                                        key={i}
                                                        active={pageNum === currentPage}
                                                        onClick={() => handlePageChange(pageNum)}
                                                    >
                                                        {pageNum}
                                                    </Pagination.Item>
                                                );
                                            })}
                                            
                                            <Pagination.Next 
                                                onClick={() => handlePageChange(currentPage + 1)}
                                                disabled={currentPage === totalPages}
                                            />
                                            <Pagination.Last 
                                                onClick={() => handlePageChange(totalPages)}
                                                disabled={currentPage === totalPages}
                                            />
                                        </Pagination>
                                    </div>
                                </Card.Footer>
                            )}
                        </Card>
                    )}

                    {/* Vue Graphique améliorée */}
                    {viewMode === 'graph' && (
                        <Card className="border-0 shadow-sm">
                            <Card.Header className="bg-white py-3 d-flex justify-content-between align-items-center">
                                <h5 className="mb-0">
                                    <i className="ri-bar-chart-2-line me-2 text-primary"></i>
                                    Évolution du chiffre d'affaires
                                </h5>
                                <div className="d-flex gap-3">
                                    <div className="d-flex align-items-center">
                                        <span className="badge bg-primary me-2" style={{ width: '12px', height: '12px' }}></span>
                                        <small>CA mensuel</small>
                                    </div>
                                    <div className="d-flex align-items-center">
                                        <span className="badge bg-success me-2" style={{ width: '12px', height: '12px' }}></span>
                                        <small>Moyenne: {formatCurrency(caMoyen)}</small>
                                    </div>
                                </div>
                            </Card.Header>
                            <Card.Body>
                                {sortedData.length === 0 ? (
                                    <div className="text-center py-5">
                                        <i className="ri-bar-chart-line fs-1 text-muted mb-3"></i>
                                        <h5>Aucune donnée à afficher</h5>
                                    </div>
                                ) : (
                                    <div className="position-relative py-4" style={{ minHeight: '350px' }}>
                                        {/* Barres du graphique */}
                                        <div className="d-flex align-items-end justify-content-around h-100">
                                            {sortedData.map((item, index) => {
                                                const hauteur = maxCA > 0 ? (item.ca / maxCA) * 200 : 0;
                                                return (
                                                    <div key={index} className="text-center" style={{ width: '50px' }}>
                                                        <OverlayTrigger
                                                            placement="top"
                                                            overlay={
                                                                <Tooltip>
                                                                    <div className="text-center">
                                                                        <strong>{item.moisLabel}</strong><br />
                                                                        CA: {formatCurrency(item.ca)}<br />
                                                                        Factures: {item.nb_factures}
                                                                    </div>
                                                                </Tooltip>
                                                            }
                                                        >
                                                            <div 
                                                                className="bg-primary rounded-top mb-2 mx-auto"
                                                                style={{ 
                                                                    height: `${hauteur}px`, 
                                                                    width: '30px',
                                                                    transition: 'height 0.3s ease',
                                                                    cursor: 'pointer',
                                                                    opacity: 0.8
                                                                }}
                                                            ></div>
                                                        </OverlayTrigger>
                                                        <small className="text-muted">{moisAbrege(item.moisLabel)}</small>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                        
                                        {/* Ligne de moyenne */}
                                        <div className="position-relative" style={{ top: '-20px' }}>
                                            <hr className="border-success border-2" style={{ 
                                                position: 'absolute', 
                                                width: '100%', 
                                                top: `${200 - (caMoyen / maxCA * 200)}px`,
                                                opacity: 0.5,
                                                borderStyle: 'dashed'
                                            }} />
                                        </div>
                                        
                                        {/* Légende */}
                                        <div className="mt-5 pt-3 border-top text-center text-muted small">
                                            <i className="ri-information-line me-1"></i>
                                            Survolez les barres pour voir les détails
                                        </div>
                                    </div>
                                )}
                            </Card.Body>
                        </Card>
                    )}

                    {/* Pied de tableau avec totaux */}
                    {viewMode === 'table' && sortedData.length > 0 && (
                        <Card className="border-0 shadow-sm mt-3 bg-light">
                            <Card.Body className="p-3">
                                <Row className="align-items-center">
                                    <Col md={6}>
                                        <div className="d-flex align-items-center text-muted small">
                                            <i className="ri-information-line me-2 fs-5 text-info"></i>
                                            <span>
                                                <strong>Total {annee}</strong> : {moisAvecCA} mois avec activité
                                            </span>
                                        </div>
                                    </Col>
                                    <Col md={6}>
                                        <div className="d-flex justify-content-end gap-4">
                                            <span className="fw-bold text-primary">{formatCurrency(totalCA)}</span>
                                            <span className="fw-bold text-info">{formatNumber(totalFactures)}</span>
                                            <span className="fw-bold text-success">{formatCurrency(caMoyen)}</span>
                                        </div>
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>
                    )}
                </div>

                <style>{`
                    @keyframes fadeIn {
                        from { opacity: 0; transform: translateY(10px); }
                        to { opacity: 1; transform: translateY(0); }
                    }

                    .fade-in {
                        animation: fadeIn 0.5s ease;
                    }

                    .stats-row .stat-card {
                        transition: all 0.3s ease;
                        border-radius: 16px;
                        overflow: hidden;
                    }

                    .stats-row .stat-card:hover {
                        transform: translateY(-5px);
                        box-shadow: 0 20px 25px -5px rgba(0,0,0,0.1) !important;
                    }

                    .stat-icon-wrapper {
                        width: 56px;
                        height: 56px;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        border-radius: 16px;
                        transition: transform 0.3s ease;
                    }

                    .stat-card:hover .stat-icon-wrapper {
                        transform: scale(1.1);
                    }

                    .filter-card {
                        border-radius: 16px;
                        background: #f8fafc;
                    }

                    .filter-select {
                        border-radius: 10px;
                        border: 1px solid #e2e8f0;
                        padding: 0.5rem;
                        transition: all 0.2s ease;
                    }

                    .filter-select:focus {
                        border-color: #4361ee;
                        box-shadow: 0 0 0 3px rgba(67, 97, 238, 0.1);
                    }

                    .table-card {
                        border-radius: 16px;
                        overflow: hidden;
                    }

                    .custom-table thead th {
                        background: #f8fafc;
                        color: #1e293b;
                        font-weight: 600;
                        font-size: 0.85rem;
                        text-transform: uppercase;
                        letter-spacing: 0.5px;
                        border-bottom: 2px solid #e2e8f0;
                        padding: 1rem 0.75rem;
                        white-space: nowrap;
                    }

                    .custom-table thead th:hover {
                        color: #4361ee;
                        background: #f1f5f9;
                    }

                    .custom-table tbody td {
                        padding: 1rem 0.75rem;
                        vertical-align: middle;
                    }

                    .custom-table tbody tr {
                        transition: all 0.2s ease;
                    }

                    .custom-table tbody tr:hover {
                        background: #f1f5f9 !important;
                    }

                    .bg-primary-soft {
                        background: rgba(67, 97, 238, 0.1);
                    }

                    .bg-success-soft {
                        background: rgba(16, 185, 129, 0.1);
                    }

                    .bg-info-soft {
                        background: rgba(13, 202, 240, 0.1);
                    }

                    .bg-warning-soft {
                        background: rgba(245, 158, 11, 0.1);
                    }

                    .tracking-wide {
                        letter-spacing: 0.5px;
                    }

                    .pagination .page-link {
                        border-radius: 8px;
                        margin: 0 2px;
                        color: #4361ee;
                        border: 1px solid #e2e8f0;
                    }

                    .pagination .page-link:hover {
                        background: #f1f5f9;
                        color: #3a56d4;
                        border-color: #cbd5e1;
                    }

                    .pagination .active .page-link {
                        background: linear-gradient(135deg, #4361ee, #3a56d4);
                        border-color: #4361ee;
                        color: white;
                    }
                `}</style>

                <Footer />
            </div>
        </>
    );
}